import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { CheckCircle2, Circle, SkipForward, Rocket, Trash2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Progress } from "@/components/ui/progress";
import { Input } from "@/components/ui/input";
import { AgenciaDefinicoes } from "@/components/AgenciaDefinicoes";
import { Marca } from "@/components/MarcaDefinicoes";
import { GestaoDefinicoes } from "@/components/GestaoDefinicoes";
import { Equipa, EntradaAutomatica } from "@/pages/Definicoes";
import { PaletteSuggest, BrandPreviews, EmailsStep, EntradaStep, DocumentosStep, EquipaImport } from "@/components/setup/SetupSteps";

const card = "bg-white rounded-[16px] p-6 shadow-[0_8px_30px_rgb(0,0,0,0.04)] border border-slate-100";

function MarcaStep() {
  const [k, setK] = useState(0);
  return <div className="space-y-6"><Marca key={`m${k}`} /><PaletteSuggest onApplied={() => setK(k + 1)} /><BrandPreviews key={`p${k}`} /></div>;
}

function Summary({ s, reload }) {
  const [phone, setPhone] = useState("");
  const [busy, setBusy] = useState(false);
  const send = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/agency/setup/test-request", { phone: phone || null });
      toast.success(data.emailed ? `Pedido de teste ${data.ref} enviado para o seu email` : `Processo ${data.ref} criado; o email não saiu`);
      reload();
    } catch (e) { toast.error(e.response?.data?.detail || "Não foi possível"); } finally { setBusy(false); }
  };
  const del = async () => { await api.delete("/agency/setup/test-request"); toast.success("Processo de teste apagado"); reload(); };
  return (
    <div className={`${card} space-y-4`} data-testid="setup-summary">
      <h3 className="font-heading text-lg text-ec-green">Resumo</h3>
      {s.missing.length ? <ul className="text-sm text-slate-600 list-disc pl-5 space-y-1" data-testid="setup-missing">{s.missing.map((m) => <li key={m}>{m}</li>)}</ul>
        : <p className="text-sm text-emerald-700" data-testid="setup-complete">Está tudo configurado.</p>}
      <div className="flex flex-wrap items-center gap-2">
        <Input value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="O seu telemóvel (se não estiver no perfil)" className="max-w-xs" data-testid="setup-test-phone" />
        <button onClick={send} disabled={busy} className="inline-flex items-center gap-2 px-4 h-10 rounded-lg text-sm font-semibold bg-ec-green text-white hover:bg-ec-greenDark disabled:opacity-60" data-testid="setup-test-send"><Rocket size={15} /> {busy ? "A enviar…" : "Enviar o 1.º pedido de documentos de teste"}</button>
        {s.test_pid && <button onClick={del} className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-red-600" data-testid="setup-test-delete"><Trash2 size={14} /> Apagar o processo de teste</button>}
      </div>
    </div>
  );
}

const BODY = {
  identidade: () => <AgenciaDefinicoes />, marca: () => <MarcaStep />, emails: () => <EmailsStep />,
  documentos: () => <DocumentosStep />, gestao: () => <GestaoDefinicoes />,
  equipa: () => <div className="space-y-6"><EquipaImport /><Equipa /><EntradaAutomatica /></div>,
};

export function SetupWizard({ open, onOpenChange, onChange }) {
  const [s, setS] = useState(null);
  const [step, setStep] = useState(0);
  const load = () => api.get("/agency/setup").then(({ data }) => { setS(data); onChange?.(data); });
  useEffect(() => { if (open) { load(); } }, [open]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!s) return null;
  const ids = [...s.steps.map((x) => x.id), "resumo"];
  const cur = ids[step];
  const next = () => { load(); setStep(Math.min(step + 1, ids.length - 1)); };
  const skip = async () => { await api.put("/agency/setup", { skip: cur }); next(); };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-6xl w-[96vw] max-h-[92vh] overflow-y-auto p-0" data-testid="setup-wizard">
        <div className="grid md:grid-cols-[240px_1fr]">
          <aside className="bg-slate-50 border-r border-slate-100 p-5 space-y-4">
            <DialogHeader><DialogTitle className="font-heading text-ec-green">Configurar a agência</DialogTitle>
              <DialogDescription>{s.done} de {s.total} passos</DialogDescription></DialogHeader>
            <Progress value={(s.done / s.total) * 100} data-testid="setup-progress" />
            <nav className="space-y-1">
              {[...s.steps, { id: "resumo", label: "Resumo" }].map((x, i) => (
                <button key={x.id} onClick={() => { load(); setStep(i); }} className={`w-full flex items-center gap-2 text-sm rounded-lg px-2.5 py-2 text-left transition-colors ${i === step ? "bg-white shadow-sm text-ec-green font-semibold" : "text-slate-600 hover:bg-white"}`} data-testid={`setup-nav-${x.id}`}>
                  {x.done ? <CheckCircle2 size={15} className="text-emerald-600" /> : x.skipped ? <SkipForward size={15} className="text-slate-400" /> : <Circle size={15} className="text-slate-300" />}{x.label}
                </button>
              ))}
            </nav>
          </aside>
          <section className="p-5 md:p-7 space-y-5 min-w-0">
            {cur === "resumo" ? <Summary s={s} reload={load} /> : cur === "entrada" ? <EntradaStep setup={s} onSaved={(d) => { setS(d); onChange?.(d); }} /> : BODY[cur]()}
            {cur !== "resumo" && (
              <div className="flex justify-end gap-2 pt-2">
                <button onClick={skip} className="px-4 h-10 rounded-lg text-sm text-slate-500 hover:bg-slate-50" data-testid="setup-skip">Saltar</button>
                <button onClick={next} className="px-5 h-10 rounded-lg text-sm font-semibold bg-ec-green text-white hover:bg-ec-greenDark" data-testid="setup-next">Seguinte</button>
              </div>
            )}
          </section>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function SetupCard() {
  const [open, setOpen] = useState(false);
  const [s, setS] = useState(null);
  useEffect(() => { api.get("/agency/setup").then(({ data }) => setS(data)).catch(() => {}); }, []);
  if (!s) return null;
  return (
    <div className={`${card} flex flex-wrap items-center gap-4 justify-between`} data-testid="setup-card">
      <div className="min-w-[240px] flex-1">
        <h3 className="font-heading text-lg text-ec-green">Configurar a agência</h3>
        <p className="text-sm text-slate-500">{s.done} de {s.total} passos concluídos{s.missing[0] ? ` · ${s.missing[0]}` : ""}</p>
        <Progress value={(s.done / s.total) * 100} className="mt-2 max-w-md" data-testid="setup-card-progress" />
      </div>
      <button onClick={() => setOpen(true)} className="px-5 h-10 rounded-lg text-sm font-semibold bg-ec-green text-white hover:bg-ec-greenDark" data-testid="setup-card-open">{s.done ? "Retomar" : "Começar"}</button>
      <SetupWizard open={open} onOpenChange={setOpen} onChange={setS} />
    </div>
  );
}

export function SetupAutoOpen({ user }) {
  const [open, setOpen] = useState(false);
  useEffect(() => {
    if (user?.role !== "direccao" || user?.agency_is_demo || user?.real_platform_admin || user?.viewing_as) { return; }
    api.get("/agency/setup").then(({ data }) => { if (!data.dismissed && data.done < data.total) { setOpen(true); } }).catch(() => {});
  }, [user?.id]); // eslint-disable-line react-hooks/exhaustive-deps
  const change = (v) => { setOpen(v); if (!v) { api.put("/agency/setup", { dismissed: true }); } };
  return open ? <SetupWizard open={open} onOpenChange={change} /> : null;
}
